import { css } from '@emotion/react';
import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { subMonths } from 'date-fns';
import { useAtomValue } from 'jotai';
import { noop } from 'lodash-es';
import React, { CSSProperties, useMemo, useState } from 'react';
import {
  last2YearsShortcut,
  lastMonthShortcut,
  lastQuarterShortcut,
  lastYearShortcut,
} from '../../../../../components/DatePicker/RangeShortcuts';
import { FieldSelect } from '../../../../../components/Form/Display/FieldSelect';
import { createCompanyDataModel, ICompanyDataModel } from '../../../../../data-models/company.data-model';
import { RendererType } from '../../../../../data-models/field-definition';
import {
  IKPIRequestDataModel,
  KPIRequestStatus,
  kpiRequestStatusToColor,
} from '../../../../../data-models/kpi-requests.data-model';
import { CompanyPeriodRequestStatus } from '../../../../../schemas/CompanyPeriodRequest.schema';
import { PersonaKey } from '../../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../../services/PermissionService';
import { kpisRequestsListStateJ } from '../../../../../services/state/KPI/KPIRequestsStateJ';
import { portcosAtomJ } from '../../../../../services/state/PortCosStateJ';
import { colors } from '../../../../../theme/colors';
import { FMT } from '../../../../../util/formatter-service';
import { createFormField } from '../../../../../view-models/form.view-model';
import { DateRangeSelector } from '../../../../Finance/form-components/DateRangeSelector';
import { KpiRequestManageViewModel } from './kpi-manage.view-model';
import { KpiRequestChart } from './KpiManageChart';
import { KpiManageGrid } from './KpiManageGrid';

const wrapperCss = css`
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
  padding: 1rem;
  row-gap: 0.5rem;
  width: 100%;
  min-width: 0;
`;

const kpiStatusMenuBarWrapperCss = css`
  align-items: center;
  column-gap: 1rem;
  display: grid;
  width: 100%;
  min-width: 0;
`;

const toggleButtonCss = css`
  &:not(:disabled) {
    &.Mui-selected {
      background-color: var(--btn-color);
      color: ${colors.primary[0]};
    }
  }
`;

const DEFAULT_DATE_RANGE_MONTHS = 4;

export function KpiRequestManageLoader() {
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>(undefined);
  const [selectedDates, setSelectedDates] = useState<[Date, Date]>(() => {
    const now = new Date();
    return [subMonths(now, DEFAULT_DATE_RANGE_MONTHS), now];
  });
  const kpiRequests = useAtomValue(kpisRequestsListStateJ);
  const portcos = useAtomValue(portcosAtomJ);
  const allStatuses = useKPIRequestStatusToShow();
  const onDateRangeChange = (from: Date | null, to: Date | null) => {
    if (from && to) {
      setSelectedDates([from, to]);
    }
  };
  const { includedCompanies, requests, requestStats } = useMemo(() => {
    if (kpiRequests && portcos) {
      return toKpiRequestsViewModel(
        kpiRequests,
        portcos,
        selectedDates[0],
        selectedDates[1],
        selectedCompanyId
      );
    }
    return { includedCompanies: new Map<number, string>(), requests: [], requestStats: new Map() };
  }, [kpiRequests, portcos, selectedCompanyId, selectedDates]);

  return (
    <KpiRequestManage
      allStatuses={allStatuses}
      onDateRangeChange={onDateRangeChange}
      includedCompanies={includedCompanies}
      onSelectedCompanyChange={setSelectedCompanyId}
      requests={requests}
      requestStats={requestStats}
      selectedCompanyId={selectedCompanyId}
    />
  );
}

type KpiManageMenuBarProps = {
  allStatuses: (KPIRequestStatus | CompanyPeriodRequestStatus)[];
  includedCompanies: Map<number, string>;
  onDateRangeChange: (from: Date | null, to: Date | null) => void;
  onSelectedStatusChange: (status: string) => void;
  onSelectedCompanyChange: (companyId: number | undefined) => void;
  requestStats: KpiRequestManageData['requestStats'];
  selectedCompanyId: number | undefined;
  selectedStatus: string;
  rightAction?: React.ReactNode;
};

export function KpiManageMenuBar({
  allStatuses,
  onDateRangeChange,
  onSelectedCompanyChange,
  onSelectedStatusChange,
  requestStats,
  selectedStatus,
  selectedCompanyId,
  includedCompanies,
  rightAction,
}: KpiManageMenuBarProps) {
  const shortcuts = useMemo(
    () => [last2YearsShortcut(), lastYearShortcut(), lastQuarterShortcut(), lastMonthShortcut()],
    []
  );

  const initialDates = useMemo(() => {
    const now = new Date();
    return {
      from: subMonths(now, DEFAULT_DATE_RANGE_MONTHS),
      to: now,
    };
  }, []);

  const _onDateRangeChange = (from: Date | null, to: Date | null) => {
    onSelectedStatusChange('All');
    onSelectedCompanyChange(undefined);
    onDateRangeChange(from, to);
  };

  const onChangeSelectedStatus = (event: React.MouseEvent<HTMLElement>, newStatus: string | null) => {
    // Prevent deselection - always keep one item selected
    if (newStatus !== null) {
      onSelectedStatusChange(newStatus);
    }
  };

  const statusToggles = allStatuses.map((status) => {
    const disabled = !requestStats.has(status);

    return (
      <ToggleButton
        key={status}
        value={status}
        disabled={disabled}
        css={toggleButtonCss}
        style={{ '--btn-color': kpiRequestStatusToColor(status) } as CSSProperties}
      >
        <span>{FMT.format('kpiStatus', status)}</span>
      </ToggleButton>
    );
  });
  statusToggles.unshift(
    <ToggleButton
      key='all'
      value='All'
      css={toggleButtonCss}
      style={{ '--btn-color': colors.primary[60] } as CSSProperties}
    >
      All
    </ToggleButton>
  );

  return (
    <div css={kpiStatusMenuBarWrapperCss}>
      <ToggleButtonGroup value={selectedStatus} onChange={onChangeSelectedStatus} exclusive fullWidth>
        {statusToggles}
      </ToggleButtonGroup>
      <div style={{ width: '100%', height: '30px', minWidth: 0 }}>
        <KpiRequestChart data={requestStats} allStatuses={allStatuses} selectedStatus={selectedStatus} />
      </div>
      <Stack
        display={'grid'}
        gap='1rem'
        gridTemplateColumns={rightAction ? '1fr 200px auto auto' : '1fr 200px auto'}
        alignItems='center'
      >
        <div style={{ justifySelf: 'start' }}>{`${requestStats.get(selectedStatus)} Requests`}</div>
        <CompanySelect
          includedCompanies={includedCompanies}
          selectedCompanyId={selectedCompanyId}
          onCompanySelected={onSelectedCompanyChange}
        />
        <DateRangeSelector
          onDateRangeChange={_onDateRangeChange}
          setSelectedStartingDate={noop}
          setSelectedEndingDate={noop}
          textContent={(selectedStartingDate, selectedEndingDate) =>
            selectedStartingDate && selectedEndingDate
              ? `${FMT.format('dateNumeric', selectedStartingDate)} - ${FMT.format(
                  'dateNumeric',
                  selectedEndingDate
                )}`
              : 'Select Date Range'
          }
          style={{ justifySelf: 'end' }}
          initialStartDate={initialDates.from}
          initialEndDate={initialDates.to}
          shortcuts={shortcuts}
        />
        {rightAction}
      </Stack>
    </div>
  );
}

type KpiRequestManageProps = KpiRequestManageData & {
  allStatuses: KPIRequestStatus[];
  onDateRangeChange: (from: Date | null, to: Date | null) => void;
  onSelectedCompanyChange: (companyId: number | undefined) => void;
  selectedCompanyId: number | undefined;
};

function KpiRequestManage(props: KpiRequestManageProps) {
  const {
    allStatuses,
    includedCompanies,
    requests,
    requestStats,
    onDateRangeChange,
    onSelectedCompanyChange,
    selectedCompanyId,
  } = props;
  const [selectedStatus, setSelectedStatus] = useState('All');

  return (
    <div css={wrapperCss}>
      <KpiManageMenuBar
        includedCompanies={includedCompanies}
        allStatuses={allStatuses}
        onDateRangeChange={onDateRangeChange}
        onSelectedCompanyChange={onSelectedCompanyChange}
        onSelectedStatusChange={setSelectedStatus}
        requestStats={requestStats}
        selectedCompanyId={selectedCompanyId}
        selectedStatus={selectedStatus}
      />
      <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', minWidth: 0 }}>
        <KpiManageGrid requests={requests} selectedStatus={selectedStatus} />
      </div>
    </div>
  );
}

function CompanySelect({
  selectedCompanyId,
  includedCompanies,
  onCompanySelected,
}: {
  selectedCompanyId: number | undefined;
  includedCompanies: Map<number, string>;
  onCompanySelected: (companyId: number | undefined) => void;
}) {
  const options = useMemo(() => {
    return Array.from(includedCompanies.entries())
      .map(([id, name]) => ({
        value: id,
        displayName: name,
      }))
      .sort((a, b) => a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase()));
  }, [includedCompanies]);

  const field = useMemo(
    () =>
      createFormField({
        key: 'company',
        label: 'company',
        renderer: RendererType.singleSelect,
        fullWidth: true,
        placeholder: 'Select Company',
        rendererMeta: {
          values: options,
        },
      }),
    [options]
  );

  return (
    <FieldSelect
      formField={field}
      formProps={{
        name: 'company',
        onBlur: noop,
        value: selectedCompanyId,
        onChange: (value: number | undefined) => {
          onCompanySelected(value);
        },
        ref: noop,
      }}
    />
  );
}

function toKpiRequestsViewModel(
  requests: IKPIRequestDataModel[],
  companies: ICompanyDataModel[],
  from: Date,
  to: Date,
  selectedCompanyId: number | undefined
) {
  const includedCompanies = new Map<number, string>();
  const companiesById = companies.reduce(
    (acc, company) => {
      acc[company.id] = company;
      return acc;
    },
    {} as Record<number, ICompanyDataModel>
  );

  const res: KpiRequestManageViewModel[] = [];
  const requestStats: Map<KPIRequestStatus | string, number> = new Map();
  const dummyCompany = createCompanyDataModel();

  const requestForPeriod = requests.filter((request) => {
    return (
      request.period == undefined ||
      (request.period >= from.toISOString() && request.period <= to.toISOString())
    );
  });

  requestForPeriod.forEach((request) => {
    const { name } = companiesById[request.companyId] ?? dummyCompany;
    includedCompanies.set(request.companyId, name);
  });

  requestForPeriod
    .filter((request) => selectedCompanyId == undefined || request.companyId === selectedCompanyId)
    .forEach((request) => {
      const { kpiRequestResponse, ...rest } = request;
      const { name, sectorId } = companiesById[request.companyId] ?? dummyCompany;
      const statusCount = requestStats.get(request.status) ?? 0;
      requestStats.set(request.status, statusCount + 1);

      res.push({
        ...rest,
        companyName: name,
        fye: request.fye ?? null,
        granularity: request.granularity ?? undefined,
        sectorId,
        responseDate: kpiRequestResponse?.respondedAt ?? undefined,
      });
    });

  requestStats.set('All', requestForPeriod.length);

  return { includedCompanies, requests: res, requestStats };
}

type KpiRequestManageData = Awaited<ReturnType<typeof toKpiRequestsViewModel>>;

export function useKPIRequestStatusToShow() {
  const isManagedServices = PermissionService.get().hasPersona(PersonaKey.managedServices);

  return useMemo(() => {
    if (isManagedServices) {
      return [
        KPIRequestStatus.ManagedServiceSubmitted,
        KPIRequestStatus.ManagedServiceInProgress,
        KPIRequestStatus.ManagedServiceFinished,
      ];
    } else {
      return [
        KPIRequestStatus.NotSent,
        KPIRequestStatus.Sent,
        KPIRequestStatus.Submitted,
        KPIRequestStatus.ManagedServiceSubmitted,
        KPIRequestStatus.ManagedServiceInProgress,
        KPIRequestStatus.ManagedServiceFinished,
        KPIRequestStatus.Rejected,
        KPIRequestStatus.Accepted,
        KPIRequestStatus.Archived,
      ];
    }
  }, [isManagedServices]);
}
